import axios from 'axios';
import { tokenConfig } from './authActions';
import configData from './../configData';
import {
  GET_ALL_SESSIONS_LOADING,
  GET_ALL_SESSIONS_SUCCESS,
  GET_ALL_SESSIONS_FAIL,
  SYNC_SESSION_LOADING,
  SYNC_SESSION_SUCCESS,
  SYNC_SESSION_FAIL,
  UPDATE_SESSION_LOADING,
  UPDATE_SESSION_SUCCESS,
  UPDATE_SESSION_FAIL,
  CLEAR_SESSION_ERRORS,
  HYDRATE_SESSION_TOOL_BUNDLES
} from './types';

/**
 * Clear session errors
 */
export const clearSessionErrors = () => ({
  type: CLEAR_SESSION_ERRORS
});

// Local cache helpers for session tool bundles
function loadBundlesCache() {
  try { return JSON.parse(localStorage.getItem('sessionToolBundlesCache') || '[]'); } catch { return []; }
}
function saveBundlesCache(bundles) {
  try { localStorage.setItem('sessionToolBundlesCache', JSON.stringify(bundles || [])); } catch {}
}
function mergeBundles(a = [], b = []) {
  const byId = new Map();
  const union = (oldArr = [], newArr = []) => {
    const seen = new Set();
    const out = [];
    [...oldArr, ...newArr].forEach(x => { const name = x?.tool || x; if (!seen.has(name)) { seen.add(name); out.push(x); } });
    return out;
  };
  [...a, ...b].forEach(bundle => {
    if (!bundle?.sessionId) return;
    const prev = byId.get(bundle.sessionId);
    if (!prev) { byId.set(bundle.sessionId, bundle); return; }
    byId.set(bundle.sessionId, {
      sessionId: bundle.sessionId,
      datetime: new Date(bundle.datetime) < new Date(prev.datetime || bundle.datetime) ? bundle.datetime : (prev.datetime || bundle.datetime),
      userEmail: bundle.userEmail || prev.userEmail || '',
      managerTools: union(prev.managerTools, bundle.managerTools),
      personalTools: union(prev.personalTools, bundle.personalTools),
      projectTools: union(prev.projectTools, bundle.projectTools)
    });
  });
  return Array.from(byId.values());
}

// Hydrate Redux from local bundle cache at app start
export const hydrateSessionToolBundles = () => (dispatch) => {
  const cached = loadBundlesCache();
  if (cached.length) dispatch({ type: HYDRATE_SESSION_TOOL_BUNDLES, payload: cached });
};

/**
 * Fetch all sessions and events from backend (Admin only)
 */
export const getAllSessions = () => (dispatch, getState) => {
  dispatch({ type: GET_ALL_SESSIONS_LOADING });

  axios
    .post(
      `${configData.SERVER_URL}getAllSessionsAndEvents`,
      {},
      tokenConfig(getState)
    )
    .then(res => {
      console.log('Data received from server:', res.data);
      // Normalize events so analytics can work regardless of backend storage shape
  const rawEvents = res.data.event || [];
  const sessions = res.data.session || [];
  const normalizedEvents = normalizeBackendEvents(rawEvents, sessions);
      // Persist detected backend event mode for writers
      try {
        const hasDetails = normalizedEvents.some(e => !!(e.details && e.details.tool && e.sessionType));
        // Consider aggregate mode only if we actually see non-empty arrays in raw events
        const hasNonEmptyArrays = (rawEvents || []).some(e => (
          (Array.isArray(e.personalTools) && e.personalTools.length > 0) ||
          (Array.isArray(e.managerTools) && e.managerTools.length > 0) ||
          (Array.isArray(e.projectTools) && e.projectTools.length > 0)
        ));
        const mode = hasDetails ? 'detail' : (hasNonEmptyArrays ? 'aggregate' : 'unknown');
        localStorage.setItem('backendEventMode', mode);
      } catch {}
      // Save merged bundles to local cache and dispatch
      const cached = loadBundlesCache();
      const mergedBundles = mergeBundles(cached, (function buildBundlesFromEvents(evts){
        // Recompute bundles from normalized events with unique tools per space
        const aggregatedMap = new Map();
        const ensureRec = (sid, seedEvt) => {
          if (!aggregatedMap.has(sid)) {
            aggregatedMap.set(sid, {
              sessionId: sid,
              datetime: seedEvt?.timestamp || new Date().toISOString(),
              managerTools: [],
              personalTools: [],
              projectTools: [],
              userEmail: seedEvt?.email || ''
            });
          }
          return aggregatedMap.get(sid);
        };
        const hasTool = (arr, tool) => arr.some(t => (t.tool || t) === tool);
        evts.forEach(evt => {
          const sid = evt.sessionId || 'unknown';
          const rec = ensureRec(sid, evt);
          const tool = (evt?.details?.tool || evt.tool || '').toString();
          const type = (evt.sessionType || '').toLowerCase();
          if (!tool) return;
          // Store names only
          if (type === 'manager_space') { if (!hasTool(rec.managerTools, tool)) rec.managerTools.push(tool); }
          else if (type === 'personal_space') { if (!hasTool(rec.personalTools, tool)) rec.personalTools.push(tool); }
          else if (type === 'project_space') { if (!hasTool(rec.projectTools, tool)) rec.projectTools.push(tool); }
          if (evt.timestamp && rec.datetime && new Date(evt.timestamp) < new Date(rec.datetime)) rec.datetime = evt.timestamp;
          if (!rec.userEmail && evt.email) rec.userEmail = evt.email;
        });
        return Array.from(aggregatedMap.values());
      })(normalizedEvents));
      saveBundlesCache(mergedBundles);
    dispatch({
        type: GET_ALL_SESSIONS_SUCCESS,
        payload: {
      sessions: res.data.session || [],
      events: normalizedEvents,
      userObj: res.data.userObj || null
        }
      });
      // Then hydrate the bundles (so reducer merges them with any existing)
      if (mergedBundles.length) {
        dispatch({ type: HYDRATE_SESSION_TOOL_BUNDLES, payload: mergedBundles });
      }
    })
    .catch(error => {
      console.error("Error - getAllSessions:", error.response || error.message);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data || 
                          error.message || 
                          'Server connection error';
      
      dispatch({
        type: GET_ALL_SESSIONS_FAIL,
        payload: errorMessage
      });
    });
};

/**
 * Create/sync a new session with backend
 */
export const syncSession = (sessionData) => (dispatch, getState) => {
  dispatch({ type: SYNC_SESSION_LOADING });

  if (!sessionData.sessionId || !sessionData.userId || !sessionData.startedAt) {
    dispatch({
      type: SYNC_SESSION_FAIL,
      payload: 'Invalid session data: sessionId, userId and startedAt are required'
    });
    return;
  }

  const payload = {
    createSession: true,
    session: {
      sessionId: sessionData.sessionId,
      email: sessionData.userId, // Backend expects 'email'
      startedAt: sessionData.startedAt,
      endedAt: sessionData.endedAt || null,
      endReason: sessionData.endReason || null,
      heartbeats: sessionData.heartbeats || [],
      activity_duration: sessionData.activity_duration || 0,
      inactivity_duration: sessionData.inactivity_duration || 0,
      ...sessionData
    }
  };

  console.log('Sending session to server:', payload);

  axios
    .post(
      `${configData.SERVER_URL}createSessionOrEvent`,
      payload,
      tokenConfig(getState)
    )
    .then(res => {
      console.log('Session synced successfully:', res.data);
      dispatch({
        type: SYNC_SESSION_SUCCESS,
        payload: res.data
      });
    })
    .catch(error => {
      console.error("Error - syncSession:", error.response || error.message);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data || 
                          error.message || 
                          'Session sync error';
      
      dispatch({
        type: SYNC_SESSION_FAIL,
        payload: errorMessage
      });
    });
};

/**
 * Update an existing session
 */
export const updateSession = (sessionData) => (dispatch, getState) => {
  dispatch({ type: UPDATE_SESSION_LOADING });

  if (!sessionData.sessionId) {
    dispatch({
      type: UPDATE_SESSION_FAIL,
      payload: 'Session ID required for update'
    });
    return;
  }

  const payload = {
    updateSession: true,
    session: {
      sessionId: sessionData.sessionId,
      email: sessionData.userId,
      endedAt: sessionData.endedAt,
      endReason: sessionData.endReason,
      activity_duration: sessionData.activity_duration,
      inactivity_duration: sessionData.inactivity_duration,
      heartbeats: sessionData.heartbeats,
      ...sessionData
    }
  };

  console.log('Updating session:', payload);

  axios
    .post(
      `${configData.SERVER_URL}createSessionOrEvent`,
      payload,
      tokenConfig(getState)
    )
    .then(res => {
      console.log('Session updated successfully:', res.data);
      dispatch({
        type: UPDATE_SESSION_SUCCESS,
        payload: res.data
      });
    })
    .catch(error => {
      console.error("Error - updateSession:", error.response || error.message);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data || 
                          error.message || 
                          'Session update error';
      
      dispatch({
        type: UPDATE_SESSION_FAIL,
        payload: errorMessage
      });
    });
};

/**
 * Sync multiple sessions in batch
 */
export const syncMultipleSessions = (sessionsArray) => (dispatch, getState) => {
  if (!Array.isArray(sessionsArray) || sessionsArray.length === 0) {
    dispatch({
      type: SYNC_SESSION_FAIL,
      payload: 'No sessions to sync'
    });
    return;
  }

  dispatch({ type: SYNC_SESSION_LOADING });

  const syncPromises = sessionsArray.map(session => 
    axios.post(
      `${configData.SERVER_URL}createSessionOrEvent`,
      {
        createSession: true,
        session: {
          ...session,
          email: session.userId
        }
      },
      tokenConfig(getState)
    )
  );

  Promise.all(syncPromises)
    .then(responses => {
      console.log(`${responses.length} sessions synced successfully`);
      dispatch({
        type: SYNC_SESSION_SUCCESS,
        payload: {
          message: `${responses.length} sessions synced`,
          sessions: responses.map(r => r.data)
        }
      });
    })
    .catch(error => {
      console.error("Error - syncMultipleSessions:", error);
      dispatch({
        type: SYNC_SESSION_FAIL,
        payload: 'Multiple sessions sync error'
      });
    });
};

// Convert backend Event documents into a flat per-tool events array understood by analytics
function normalizeBackendEvents(events = [], sessions = []) {
  const out = [];
  const sessionEmailById = new Map();
  try {
    (sessions || []).forEach(s => {
      if (s && (s._id || s.id)) {
        sessionEmailById.set(String(s._id || s.id), s.email || s.userId || '');
      }
    });
  } catch {}
  const toDate = (val) => {
    try {
      const d = val ? new Date(val) : null;
      return d && !isNaN(d.getTime()) ? d.toISOString() : new Date().toISOString();
    } catch { return new Date().toISOString(); }
  };

  (events || []).forEach(evt => {
    if (!evt) return;
    // Case 1: already per-tool event shape
    // If this document is an aggregate wrapper (details.tool === 'aggregate'),
    // don't push it as a single event; we'll expand arrays below instead.
    const hasDetail = !!(evt.details && evt.details.tool && evt.sessionType);
    const isAggregateWrapper = hasDetail && String(evt.details.tool).toLowerCase() === 'aggregate';
    const hasAnyArray = (
      (Array.isArray(evt.personalTools) && evt.personalTools.length > 0) ||
      (Array.isArray(evt.projectTools) && evt.projectTools.length > 0) ||
      (Array.isArray(evt.managerTools) && evt.managerTools.length > 0)
    );
    if (hasDetail && !isAggregateWrapper && !hasAnyArray) {
      out.push({
        _id: evt._id,
        sessionId: evt.sessionId,
        sessionType: evt.sessionType,
        details: { tool: evt.details.tool },
        timestamp: evt.timestamp || evt.createdAt || evt.date || new Date().toISOString(),
        email: evt.email || evt.userId || sessionEmailById.get(String(evt.sessionId || '')) || ''
      });
      // Do not return; continue to process arrays if any (usually none for real detail events)
    }

    // Case 2: legacy/aggregate shape with arrays
    const pushTools = (arr, type) => {
      if (!Array.isArray(arr) || arr.length === 0) return;
      arr.forEach(t => {
        const toolName = typeof t === 'string' ? t : (t?.tool || t?.name || null);
        if (!toolName) return;
        out.push({
          sessionId: evt.sessionId || evt._id,
          sessionType: type,
          details: { tool: toolName },
          timestamp: evt.timestamp || evt.createdAt || evt.date || new Date().toISOString(),
          email: evt.email || evt.userId || sessionEmailById.get(String(evt.sessionId || evt._id || '')) || ''
        });
      });
    };

  pushTools(evt.personalTools, 'personal_space');
  pushTools(evt.projectTools, 'project_space');
  pushTools(evt.managerTools, 'manager_space');
  });

  return out;
}